import React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody } from '@material-ui/core';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TablePagination from '@mui/material/TablePagination';

import { Action, ActivityStatus, IncentiveType } from 'shared/types';
import { TableBox } from 'retired/Box';
import ExternalLinkIcon from 'assets/icons/external-link.svg';
import Img from 'retired/Img';
import { getAppDomain } from 'utils/getAppDomain';
import Organization from 'entities/organization/model';
import { ActionVisibilityStatus } from 'generated/graphql';

import { ActionChip } from './ActionChip';

const getRewardCopy = (action: Action) => {
  const incentiveData = action.incentives.filter(
    (incentive) => incentive.type === IncentiveType.Reward
  )[0];

  if (!incentiveData) {
    return '-';
  }

  const unit =
    incentiveData.unit === 'tree' && incentiveData.amount > 1
      ? `${incentiveData.unit}s`
      : incentiveData.unit;

  return `${incentiveData.amount} ${unit}`;
};

const getImpactCopy = (action: Action) => {
  const incentiveData = action.incentives.filter(
    (incentive) => incentive.type === IncentiveType.Impact
  )[0];
  if (!incentiveData) {
    return '-';
  }

  if (incentiveData.unit === 'co2') {
    return `${incentiveData.amount} kg CO\u{2082}`;
  }

  return `${incentiveData.amount} ${incentiveData.unit}`;
};

export const ActionTable = ({
  actions,
  showInactive,
  organization,
  actionsTotal,
  actionsPage,
  actionsLimit,
  handlePageChange,
  handleRowsPerPageChange,
}: {
  actions: Action[];
  showInactive: boolean;
  organization: Organization;
  actionsTotal: number;
  actionsPage: number;
  actionsLimit: number;
  handlePageChange: (event: unknown, newPage: number) => void;
  handleRowsPerPageChange: (event: any) => void;
}) => (
  <TableBox>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell size="medium">Name</TableCell>
          <TableCell size="medium">Campaign</TableCell>
          <TableCell size="medium">Nonprofit</TableCell>
          <TableCell size="medium">Start date</TableCell>
          <TableCell size="medium">End date</TableCell>
          <TableCell size="medium">Reward / Impact</TableCell>
          <TableCell size="medium">Joined / Completed</TableCell>
          <TableCell size="medium">Duplicate</TableCell>
          {!showInactive && <TableCell size="medium">Status</TableCell>}
          {!showInactive && <TableCell size="medium">View in app</TableCell>}
        </TableRow>
      </TableHead>

      <TableBody>
        {actions?.map((action) => (
          <TableRow key={action.id}>
            <TableCell>
              <Link to={`/actions/edit/${action.id}`}>{action.title}</Link>
            </TableCell>
            <TableCell>
              {action.Campaigns[0]?.title ? (
                <Link to={`/campaigns/edit/${action.Campaigns[0]?.id}`}>
                  {action.Campaigns[0]?.title}
                </Link>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{action.Nonprofit?.name ?? '-'}</TableCell>
            <TableCell>
              {action.startingAt
                ? format(legacyParse(action.startingAt), convertTokens('MMM DD, YYYY'))
                : '-'}
            </TableCell>
            <TableCell>
              {action.endingAt
                ? format(legacyParse(action.endingAt), convertTokens('MMM DD, YYYY'))
                : '-'}
            </TableCell>
            <TableCell style={{ whiteSpace: 'nowrap' }}>
              {getRewardCopy(action)} / {getImpactCopy(action)}
            </TableCell>
            <TableCell>
              {
                action.Activities.filter((activity) => activity.status === ActivityStatus.Joined)
                  .length
              }{' '}
              /{' '}
              {
                action.Activities.filter((activity) => activity.status === ActivityStatus.Completed)
                  .length
              }
            </TableCell>
            <TableCell size="medium">
              <Link
                to={`/actions/edit/${action.id}?duplicate=1`}
                style={{
                  padding: 20,
                }}
              >
                <ContentCopyIcon />
              </Link>
            </TableCell>
            {!showInactive && (
              <TableCell>
                <ActionChip status={action.status} />
              </TableCell>
            )}
            {!showInactive && (
              <TableCell>
                {action.status === ActionVisibilityStatus.Published ? (
                  <a
                    target="_blank"
                    href={`${getAppDomain([organization])}/action/${action.id}`}
                    rel="noreferrer"
                  >
                    <Img
                      src={ExternalLinkIcon}
                      alt="External Link"
                      style={{
                        marginLeft: 20,
                        width: 16,
                        height: 16,
                      }}
                    />
                  </a>
                ) : (
                  '-'
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>

    <TablePagination
      component="div"
      count={actionsTotal}
      page={actionsPage}
      rowsPerPage={actionsLimit}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  </TableBox>
);
